import type {
  ClassroomDisciplineListItemViewModel,
  EClassRoomDisciplinesStatus,
} from '@/shared/api/generated';

import { formatClassPlanningDate, formatClassPlanningText } from './classPlanningFormat';

/** Rótulos de cabeçalho usados na exportação da listagem. */
export interface ClassPlanningCsvLabels {
  readonly module: string;
  readonly date: string;
  readonly discipline: string;
  readonly classStudent: string;
  readonly teacher: string;
  readonly status: string;
}

/**
 * Escapa um valor para uma célula CSV, protegendo separador, aspas e quebras de linha.
 */
function escapeCsvCell(value: string): string {
  const needsQuoting = /[";\n\r]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuoting ? `"${escaped}"` : escaped;
}

/**
 * Monta o conteúdo CSV da listagem de planejamento de aulas (todas as páginas do
 * filtro atual, não só a página visível).
 *
 * Usa `;` como separador (compatível com Excel pt-BR) e os rótulos de status
 * traduzidos fornecidos via `statusLabel`.
 *
 * @param rows        - Planejamentos a exportar (já buscados de todas as páginas)
 * @param labels      - Rótulos de cabeçalho traduzidos
 * @param statusLabel - Resolve o rótulo traduzido de um status
 * @returns Conteúdo CSV pronto para download (sem BOM)
 */
export function buildClassPlanningCsv(
  rows: readonly ClassroomDisciplineListItemViewModel[],
  labels: ClassPlanningCsvLabels,
  statusLabel: (status: EClassRoomDisciplinesStatus) => string,
): string {
  const header = [
    labels.module,
    labels.date,
    labels.discipline,
    labels.classStudent,
    labels.teacher,
    labels.status,
  ];

  const body = rows.map(row => [
    formatClassPlanningText(row.classModuleName),
    formatClassPlanningDate(row.dateStart),
    formatClassPlanningText(row.disciplineName),
    formatClassPlanningText(row.classStudentName),
    formatClassPlanningText(row.teacherName),
    row.status !== undefined ? statusLabel(row.status) : '',
  ]);

  return [header, ...body].map(cells => cells.map(escapeCsvCell).join(';')).join('\r\n');
}
